export interface SeatMember {
  email?: string;
  status?: string;
  expires_at?: string;
}

export interface SeatTotals {
  totalSeats: number;
  usedSeats: number;
  freeSeats: number;
}

const isPendingAndValid = (member: SeatMember) => {
  if (member.status !== "pending") return false;
  if (!member.expires_at) return true;
  return new Date(member.expires_at) > new Date();
};

export const calculateUsedSeats = (members: SeatMember[]): number => {
  if (!members || members.length === 0) return 0;
  return members.filter(
    (member) =>
      member?.email &&
      (member.status === "active" || isPendingAndValid(member))
  ).length;
};

export const calculateSeatTotals = (
  seatCount: number,
  members: SeatMember[]
): SeatTotals => {
  // We always include one free seat in the total.
  // `seatCount` represents purchased seats; total seats = purchased + 1 free.
  const purchasedSeats = seatCount || 0;
  const safeTotal = Math.max(purchasedSeats + 1, 1);
  const usedSeats = calculateUsedSeats(members);
  const freeSeats = Math.max(safeTotal - usedSeats, 0);

  return {
    totalSeats: safeTotal,
    usedSeats,
    freeSeats,
  };
};

export const seatAvailabilityCopy = (
  totals: SeatTotals,
  hasMembers: boolean
): string => {
  if (totals.freeSeats <= 0) {
    return "You’ve used all your seats. Add seats to invite more team members.";
  }

  if (!hasMembers && totals.totalSeats === 1) {
    return "You have 1 free seat available.";
  }

  if (totals.freeSeats === 1) {
    return "1 unused seat remaining.";
  }

  return `${totals.freeSeats} unused seats remaining.`;
};

export const formatSeatChange = (current: number, next: number) =>
  `Seats: ${current} \u2192 ${next}`;

export const validateSeatChange = (
  nextSeats: number,
  totals: SeatTotals
): string | null => {
  if (nextSeats < 1) return "Seat count must be at least 1.";
  if (nextSeats < totals.usedSeats) {
    return `You have ${totals.usedSeats} active members, so you can’t reduce seats below ${totals.usedSeats}.`;
  }
  if (nextSeats === totals.totalSeats) return "Seat count is unchanged.";
  return null;
};

